import { useEffect, useMemo, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { getFoldersPaginated, getChildFolders } from '../../../../redux/slices/lists/foldersSlice';
import Chevron from 'components/Chevron/Chevron';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import TableViewLayout from 'layouts/TableViewLayout';

import '../zones/dropdownStyle.css';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

const toActionEntity = (row: any): ActionEntity => ({ type: 'folder', id: row.folder_id, name: row.name });

const ListFolders = () => {
  const dispatch = useAppDispatch();

  const { folders, config: headerConfig, filtersConfig, pagination, status, error, loading, childFolders } = useAppSelector((state) => state.listOfFolders);

  const userData = useAppSelector((state) => state.auth.userData);

  const pageName = 'folders';
  const [showSidebar, setShowSidebar] = useState(false);

  // Expand/collapse state
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<number>>(new Set());

  const isLoading = loading.getFoldersPaginated;

  useEffect(() => {
    if (status === null) {
      dispatch(getFoldersPaginated({}) as any);
    }
  }, [dispatch, status]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: getFoldersPaginated,
    getFormattedRow: (row: any) => ({ ...row }),
  });

  const toggleExpand = (folderId: number) => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
        if (!childFolders[folderId]) {
          dispatch(getChildFolders(folderId) as any);
        }
      }
      return next;
    });
  };

  const EXPAND_COLUMN_WIDTH = 32;

  const expandColumn = {
    id: 'expand',
    header: '',
    size: EXPAND_COLUMN_WIDTH,
    minSize: EXPAND_COLUMN_WIDTH,
    maxSize: EXPAND_COLUMN_WIDTH,
    meta: {
      headerProps: {
        style: { width: EXPAND_COLUMN_WIDTH, minWidth: EXPAND_COLUMN_WIDTH },
      },
      cellProps: {
        style: {
          width: EXPAND_COLUMN_WIDTH,
          minWidth: EXPAND_COLUMN_WIDTH,
          boxSizing: 'border-box' as const,
        },
      },
    },
    cell: ({ row }: any) => {
      const rowData = row.original;
      return (
        <div
          style={{
            width: EXPAND_COLUMN_WIDTH,
            minWidth: EXPAND_COLUMN_WIDTH,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {rowData.child_count > 0 ? (
            <Chevron expanded={expandedFolderIds.has(rowData.folder_id)} onToggle={() => toggleExpand(rowData.folder_id)} className="fs-10" />
          ) : null}
        </div>
      );
    },
  };

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh });
      return <ActionDropdown actions={actions} items={['edit', 'delete', 'addToTransportFolder']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });
  const columns = [expandColumn, ...baseColumns, actionsColumn];

  // Build table data with child rows inserted after expanded parents
  const formattedData = useMemo(() => {
    const baseData = getFormattedData(folders || []);
    const result: any[] = [];

    baseData.forEach((row: any) => {
      result.push(row);
      if (expandedFolderIds.has(row.folder_id) && childFolders[row.folder_id]) {
        childFolders[row.folder_id].forEach((child: any) => {
          result.push({ ...child, _isChild: true });
        });
      }
    });

    return result;
  }, [folders, expandedFolderIds, childFolders]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => `${row.folder_id}:${(row as any)._isChild ? 'child' : 'root'}`,
  });

  const { currentPage, totalPages, totalItems } = pagination;

  // De-dupe by folder_id: a folder can appear as both a root row and a child of
  // an expanded parent, so the same id may be selected twice.
  const selectionEntities = Array.from(new Map(table.getSelectedRowModel().rows.map((r: any) => [r.original.folder_id, toActionEntity(r.original)])).values());
  const toolbarActions = getActions({ entities: selectionEntities, refresh });

  return (
    <>
      <style>{`.folder-child-row td { padding-left: 24px; }`}</style>
      <TableViewLayout
        filtersConfig={filtersConfig}
        filters={filters}
        pageName={pageName}
        columns={(headerConfig ?? []).map((c: any) => ({
          key: c.header_key,
          label: c.label,
        }))}
        pageSize={limit}
        error={error}
        onFilterChange={setFilterAndSync}
        onResetFilters={resetFilters}
        onPageSizeChange={updateLimit}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={updatePage}
        table={table}
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        onRefresh={refresh}
        isLoading={isLoading}
        isSilentRefresh={isSilentRefresh}
        rowClassName={(row) => ((row.original as any)._isChild ? 'folder-child-row' : undefined)}
        toolbarButtons={
          <>
            <ActionButton actions={toolbarActions} action="createFolder" variant="primary" />
            <ActionButton actions={toolbarActions} action="addToTransportFolder" className="ms-2" variant="secondary" label="Add selected to transport" />
          </>
        }
      />
    </>
  );
};

export default ListFolders;
